import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Avatar,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  MonetizationOn as CommissionIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as ReportsIcon,
  Schedule as TimeIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  AdminPanelSettings as AdminIcon,
  History as HistoryIcon
} from '@mui/icons-material';
// Mock data removed - will fetch from API

const RepCommission = ({ currentUser }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');

  // Generate commission history for past 12 months
  const commissionHistory = useMemo(() => {
    const history = [];
    const currentDate = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const month = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthName = month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      // Simulate sales data for each month
      const monthlySales = Math.floor(Math.random() * 300000) + 100000;
      const commissionThreshold = currentUser?.commissionThreshold || 200000;
      const commissionRate = currentUser?.commissionRate || 25;
      const commissionEarned = monthlySales >= commissionThreshold;
      const commissionAmount = commissionEarned ? (monthlySales * commissionRate / 100) : 0;
      const isLocked = i > 0; // Current month is not locked
      const isCurrentMonth = i === 0;
      
      history.push({
        period: monthName,
        month: month.getMonth(),
        year: month.getFullYear(),
        sales: monthlySales,
        commissionEarned,
        commissionAmount,
        isLocked,
        isCurrentMonth,
        lockDate: isLocked ? new Date(month.getFullYear(), month.getMonth() + 1, 1) : null
      });
    }
    
    return history;
  }, [currentUser]);

  // Get current month data
  const currentMonthData = commissionHistory.find(item => item.isCurrentMonth);
  const commissionProgress = Math.min((currentMonthData?.sales / (currentUser?.commissionThreshold || 200000)) * 100, 100);

  // Calculate YTD totals
  const ytdSales = commissionHistory.reduce((sum, item) => sum + item.sales, 0);
  const ytdCommission = commissionHistory.reduce((sum, item) => sum + item.commissionAmount, 0);
  const monthsWithCommission = commissionHistory.filter(item => item.commissionEarned).length;

  const CommissionCard = ({ title, value, subtitle, icon, color = '#0d9488', status }) => (
    <Card sx={{ height: '100%', position: 'relative' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color, mb: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {status && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {status === 'locked' ? (
                  <Chip
                    icon={<LockIcon />}
                    label="Locked"
                    size="small"
                    sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 600 }}
                  />
                ) : status === 'unlocked' ? (
                  <Chip
                    icon={<UnlockIcon />}
                    label="Unlocked"
                    size="small"
                    sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 600 }}
                  />
                ) : (
                  <Chip
                    icon={<TimeIcon />}
                    label="In Progress"
                    size="small"
                    sx={{ bgcolor: '#fef3c7', color: '#d97706', fontWeight: 600 }}
                  />
                )}
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const UnlockRequestDialog = () => (
    <Dialog open={showUnlockDialog} onClose={() => setShowUnlockDialog(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AdminIcon sx={{ mr: 2, color: '#0d9488' }} />
          Request Commission Unlock
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Commission unlock requests require administrator approval. This action will notify your manager.
          </Typography>
        </Alert>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Reason for unlock request"
          placeholder="Please provide a detailed reason for requesting to unlock this commission period..."
          value={unlockReason}
          onChange={(e) => setUnlockReason(e.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowUnlockDialog(false)}>Cancel</Button>
        <Button 
          variant="contained" 
          disabled={!unlockReason.trim()}
          sx={{ bgcolor: '#0d9488' }}
          onClick={() => {
            // Here would be the API call to request unlock
            setShowUnlockDialog(false);
            setUnlockReason('');
          }}
        >
          Submit Request
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
          Commission Tracker
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track your commission earnings and view monthly lock status
        </Typography>
      </Box>

      {/* Current Month Status */}
      <Alert 
        severity={currentMonthData?.commissionEarned ? "success" : "info"} 
        sx={{ mb: 4 }}
        icon={currentMonthData?.commissionEarned ? <CheckIcon /> : <TimeIcon />}
      >
        <Typography variant="body2">
          <strong>Current Month Status:</strong> {' '}
          {currentMonthData?.commissionEarned 
            ? `🎉 You've earned ${currentUser?.commissionRate}% commission this month! Commission will lock on the 1st of next month.`
            : `You need $${((currentUser?.commissionThreshold || 200000) - (currentMonthData?.sales || 0)).toLocaleString()} more in sales to earn ${currentUser?.commissionRate}% commission.`
          }
        </Typography>
      </Alert>

      {/* Commission Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <CommissionCard
            title="Current Month"
            value={`$${(currentMonthData?.commissionAmount || 0).toLocaleString()}`}
            subtitle={`${currentUser?.commissionRate}% of $${(currentMonthData?.sales || 0).toLocaleString()}`}
            icon={<CommissionIcon />}
            color="#0d9488"
            status={currentMonthData?.isLocked ? 'locked' : 'in_progress'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CommissionCard
            title="YTD Commission"
            value={`$${ytdCommission.toLocaleString()}`}
            subtitle={`${monthsWithCommission} qualifying months`}
            icon={<TrendingUpIcon />}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CommissionCard
            title="YTD Sales"
            value={`$${ytdSales.toLocaleString()}`}
            subtitle="Total sales volume"
            icon={<ReportsIcon />}
            color="#0f766e"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CommissionCard
            title="Commission Rate"
            value={`${currentUser?.commissionRate || 25}%`}
            subtitle={`$${(currentUser?.commissionThreshold || 200000).toLocaleString()} threshold`}
            icon={<HistoryIcon />}
            color="#0d9488"
          />
        </Grid>
      </Grid>

      {/* Current Month Progress */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Current Month Progress
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progress to {currentUser?.commissionRate}% Commission
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {commissionProgress.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={commissionProgress} 
              sx={{ 
                height: 12, 
                borderRadius: 6,
                bgcolor: '#e5e7eb',
                '& .MuiLinearProgress-bar': {
                  bgcolor: currentMonthData?.commissionEarned ? '#10b981' : '#0d9488',
                  borderRadius: 6
                }
              }} 
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                $0
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ${(currentUser?.commissionThreshold || 200000).toLocaleString()} Target
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                Current Sales: ${(currentMonthData?.sales || 0).toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Commission locks automatically on the 1st of next month
              </Typography>
            </Box>
            {currentMonthData?.commissionEarned && (
              <Chip
                icon={<CheckIcon />}
                label="Commission Earned"
                sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 600 }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Commission History */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Commission History
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                sx={{ borderColor: '#0d9488', color: '#0d9488' }}
              >
                Export History
              </Button>
            </Box>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Period</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Sales</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Commission</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Rate</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Lock Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {commissionHistory.map((item, index) => (
                  <TableRow key={index} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                    <TableCell sx={{ color: '#0f172a', fontWeight: 600 }}>
                      {item.period}
                      {item.isCurrentMonth && (
                        <Chip
                          label="Current"
                          size="small"
                          sx={{ ml: 1, bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 600 }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      ${item.sales.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ color: '#0f172a', fontWeight: 600 }}>
                      ${item.commissionAmount.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      {item.commissionEarned ? `${currentUser?.commissionRate}%` : '0%'}
                    </TableCell>
                    <TableCell>
                      {item.isLocked ? (
                        <Chip
                          icon={<LockIcon />}
                          label="Locked"
                          size="small"
                          sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 600 }}
                        />
                      ) : (
                        <Chip
                          icon={<TimeIcon />}
                          label="In Progress"
                          size="small"
                          sx={{ bgcolor: '#fef3c7', color: '#d97706', fontWeight: 600 }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#374151' }}>
                      {item.lockDate ? item.lockDate.toLocaleDateString() : 'TBD'}
                    </TableCell>
                    <TableCell>
                      {item.isLocked && item.commissionAmount > 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AdminIcon />}
                          onClick={() => setShowUnlockDialog(true)}
                          sx={{ 
                            borderColor: '#f59e0b', 
                            color: '#f59e0b',
                            fontSize: '0.75rem'
                          }}
                        >
                          Request Unlock
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Commission Structure Info */}
      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Commission Structure
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: '#f0fdfa', border: '1px solid #0d9488' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#0d9488', mb: 1 }}>
                  Commission Rate
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                  {currentUser?.commissionRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Earned when monthly sales exceed ${(currentUser?.commissionThreshold || 200000).toLocaleString()}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: '#fefce8', border: '1px solid #f59e0b' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#f59e0b', mb: 1 }}>
                  Monthly Lock Policy
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#0f172a', mb: 1 }}>
                  Auto-Lock on 1st
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Commission amounts are automatically locked on the 1st of each month and require admin approval to modify
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Unlock Request Dialog */}
      <UnlockRequestDialog />
    </Box>
  );
};

export default RepCommission;